import React, { useEffect, useContext, useRef } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { keepQuickList, copySharedQuickList, getQuickListByShareGuid } from "requests/quickList";
import { DialogContext } from "components/Shared/DialogProvider.tsx";

const sharedQuickListTitle = "Shared Quick List";
const copyQuickListTitle = "Copy Quick List Group";

const KeepSharedList = ({ history }) => {
  const dialog = useContext(DialogContext);
  const mounted = useRef(true);
  const guidFetchCount = useRef(0);

  const dialogPromise = (dialogMethod /* "ok" : "yesno" */, title, message) => {
    if (dialogMethod.toLowerCase() === "yesno") return new Promise(resolve => dialog.showYesNo(title, message, () => resolve(true), () => resolve(false)));
    if (dialogMethod.toLowerCase() === "ok") return new Promise(resolve => dialog.showOk(title, message, resolve));
  };

  const handleSharingQuickLists = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const shareGuid = urlParams.get("shareGuid");
    let shareGuids;

    if (shareGuid) shareGuids = [shareGuid];
    else shareGuids = urlParams.get("shareGuids").split(",");

    guidFetchCount.current = shareGuids.length;
    
    let isGroupCopy = urlParams.get("copy") == "1";

    let dialogTitle = isGroupCopy ? copyQuickListTitle : sharedQuickListTitle;

    dialog.showOk(dialogTitle, "Loading Quick List information...");
    let listInfo = await Promise.all(shareGuids.map(g => new Promise((resolve, reject) => getQuickListByShareGuid(g, data => resolve({ shareGuid: g, ...data }), mounted, () => reject()))));

    // if you already have access you created it or you already have it shared with you via a link that you clicked or a right
    let accessibleLists = listInfo.filter(ql => ql.doesSignedInUserHaveAccess);
    
    // if the template comes back null that means you dont have the right to view the quicklist
    let noTemplate = listInfo.filter(ql => !ql.template);

    let keepLists = listInfo.filter(ql => !ql.doesSignedInUserHaveAccess && ql.template);

    if (accessibleLists.length > 0) {
      let listNames = accessibleLists.map(ql => ql.name).join(", ");
      let plural = accessibleLists.length !== 1;
      
      await dialogPromise("ok", dialogTitle, `You already have ${plural ? "these" : "this"} shared quick ${plural ? "lists" : "list"} (${listNames})`);
    }
    
    if (noTemplate.length > 0) {
      let plural = noTemplate.length !== 1;
      await dialogPromise("ok", dialogTitle, `You do not have access rights to ${plural ? "some of these quick lists" : "this quick list"}. Please contact the user who sent you the link for more information.`);
    }

    if (keepLists.length > 0) {
      const listsByGroup = keepLists.reduce((acc, ql) => {
        const groupName = ql.group || "Unknown";

        if (!acc[groupName]) {
          acc[groupName] = [];
        }
        
        acc[groupName].push(ql.name);
        return acc;
      }, {});
    
      let dialogMessage;
    
      if (isGroupCopy) {
        dialogMessage = (
          <>
            Would you like to copy the following group & lists to your account?
            {Object.keys(listsByGroup).map((group) => (
              <div key={group} style={{ marginTop: "8px" }}>
                <br />
                {group}:
                {listsByGroup[group].map((name) => (
                  <div key={name}>- {name}</div>
                ))}
              </div>
            ))}
          </>
        );
      } else {
        dialogMessage = `Would you like to keep this shared quick list ${keepLists[0].name} in the shared tab?`;
      }

      let shouldKeepLists = await dialogPromise(
        "yesno",
        dialogTitle,
        dialogMessage
      );

      if (shouldKeepLists) {
        const shareCopyMethod = isGroupCopy ? copySharedQuickList : keepQuickList;
        await Promise.all(keepLists.map(ql => new Promise((resolve, reject) => shareCopyMethod(ql.shareGuid, resolve, mounted, reject))));
      }
    }

    history.push("/");
  };
  
  useEffect(() => {
    handleSharingQuickLists();
    return () => mounted.current = false;
  }, []);

  return <div/>;
};

KeepSharedList.propTypes = {
  history: PropTypes.object,
};

export default withRouter(KeepSharedList);
